import React from 'react';
import { Box, useToken } from '@chakra-ui/react';
import {
  FilterSelector,
  Heading,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Badge } from 'components/ui/badge';
import { Plus } from 'lucide-react';
import { IconButton } from '../../Form/IconButton';
import { Drawer } from '../../Drawer';
import { Loader } from '../../Loader';
import { BLOCK_TYPE, BlockArchetype, BlockType } from '../../../models/block';
import { BlockDrawerContent } from './BlockDrawerContent';
import { useOverviewPage } from './hooks/useOverviewPage';
import LaneBlockCard from './LaneBlockCard';
import VisitBlock from './VisitBlock';
import { archivedFilterOptions } from './constants';
import GutCheckCountCard from './GutCheckCountCard';
import SymptomScoreCard from './SymptomScoreCard';
import { ProviderType } from '../../../api/types';

type Props = {
  onOpenAppointments: (providerType?: ProviderType) => void;
  setActiveIndex?: (activeIndex: number) => void;
};

export const OverviewPage = ({ onOpenAppointments, setActiveIndex }: Props) => {
  const {
    activeAssignedBlockNames,
    drawerError,
    drawerHeader,
    drawerMode,
    drawerSubtitle,
    isDrawerLoading,
    isLoading,
    isBlockDrawerOpen,
    isUserBlocksError,
    laneLabelByType,
    lanes,
    blockOptions,
    closeBlockDrawer,
    handleUpdateUserBlock,
    handleUserBlockAction,
    onBackToBlockList,
    onCreateBlock,
    onPatientInstructionsChange,
    onSelectBlock,
    openCreateBlockDrawer,
    openEditBlockDrawer,
    openNutritionBlockDetailsDrawer,
    patientInstructions,
    nutritionBlockDetails,
    selectedBlock,
    selectedLaneBlock,
    archivedFilter,
    setArchivedFilter,
  } = useOverviewPage();
  const [secondary200] = useToken('colors', ['secondary.200']);

  if (isLoading) {
    return (
      <Box py={2}>
        <Loader loading />
      </Box>
    );
  }

  const drawerHeaderActions: {
    title: string;
    onPress: () => void;
    primary: boolean;
    disabled?: boolean;
  }[] = selectedLaneBlock?.id
    ? [
        ...(selectedLaneBlock.archetype !== BlockArchetype.Goal
          ? [
              {
                title: 'Mark Complete',
                onPress: () =>
                  handleUserBlockAction('end', selectedLaneBlock.id),
                primary: true,
                disabled: !!selectedLaneBlock?.date_ended,
              },
            ]
          : []),
        {
          title: 'Archive',
          onPress: () => handleUserBlockAction('archive', selectedLaneBlock.id),
          primary: false,
          disabled: !!selectedLaneBlock?.date_archived,
        },
      ]
    : [];

  return (
    <>
      <VStack px={6} pb={6}>
        <HStack gap={4} mb={8}>
          <HStack w={'60%'} gap={4}>
            <SymptomScoreCard onClick={() => setActiveIndex?.(1)} />
            <GutCheckCountCard />
          </HStack>
          <VisitBlock onOpenAppointments={onOpenAppointments} />
        </HStack>

        <Heading
          color={'secondary.500'}
          fontSize={'2xl'}
          fontWeight={'normal'}
          textAlign={'left'}
          mb={4}
        >
          Care Guide
        </Heading>

        <HStack justifyContent="flex-end" w="100%" mb={4}>
          <FilterSelector
            options={archivedFilterOptions}
            selected={archivedFilter}
            onChange={setArchivedFilter}
          />
        </HStack>

        {isUserBlocksError && (
          <Text color={'error.600'} mb={3}>
            We couldn't load blocks for this user. Please try again later.
          </Text>
        )}

        <HStack alignItems={'flex-start'} overflow={'auto'} pb={1} gap={4}>
          {(Object.entries(laneLabelByType) as [BlockType, string][]).map(
            ([laneKey, laneLabel]) => {
              const laneBlocks = lanes[laneKey];
              const canAddBlocks = laneKey !== BLOCK_TYPE.Clinical;

              return (
                <VStack
                  key={laneKey}
                  bgColor={'feather.100'}
                  borderRadius={'2xl'}
                  flex={1}
                  minW={'250px'}
                  overflow={'hidden'}
                  p={3}
                >
                  <HStack
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    px={2}
                    py={1}
                  >
                    <HStack alignItems={'center'}>
                      <Text
                        color={'secondary.500'}
                        fontSize={'md'}
                        fontFamily={'body'}
                        fontStyle={'normal'}
                        fontWeight={'medium'}
                        textAlign={'left'}
                      >
                        {laneLabel}
                      </Text>

                      <Badge
                        bgColor={'secondary.100'}
                        ml={2}
                        rounded={'md'}
                        px={2}
                        py={1}
                      >
                        <Text
                          color={'white'}
                          fontSize={'xs'}
                          fontWeight={'bold'}
                        >
                          {laneBlocks?.length}
                        </Text>
                      </Badge>
                    </HStack>

                    {canAddBlocks && (
                      <IconButton
                        onClick={() => {
                          openCreateBlockDrawer(laneKey);
                        }}
                      >
                        <Plus
                          color={secondary200}
                          size={16}
                          strokeWidth={2.5}
                        />
                      </IconButton>
                    )}
                  </HStack>

                  <VStack bgColor={'transparent'} pt={3} gap={3}>
                    {!laneBlocks?.length && (
                      <Text
                        color={'blueGray.600'}
                        fontSize={'sm'}
                        px={'8px'}
                        fontStyle={'italic'}
                      >
                        No blocks.
                      </Text>
                    )}

                    {laneBlocks?.map((block) => {
                      return (
                        <LaneBlockCard
                          enableCompletedBanner={!archivedFilter.value}
                          handleArchive={() =>
                            handleUserBlockAction('archive', block.id)
                          }
                          key={block.id}
                          block={block}
                          openEditBlockDrawer={openEditBlockDrawer}
                          openNutritionBlockDetailsDrawer={
                            openNutritionBlockDetailsDrawer
                          }
                        />
                      );
                    })}
                  </VStack>
                </VStack>
              );
            }
          )}
        </HStack>
      </VStack>

      <Drawer
        bgColor={'white'}
        header={drawerHeader}
        headerSubtitle={drawerSubtitle}
        headerActions={drawerHeaderActions}
        isOpen={isBlockDrawerOpen}
        onClose={closeBlockDrawer}
        showBackButton={false}
        showExternalCloseHandle={false}
        renderContent={() => (
          <BlockDrawerContent
            activeAssignedBlockNames={activeAssignedBlockNames}
            drawerError={drawerError}
            editableBlock={selectedLaneBlock}
            isDrawerLoading={isDrawerLoading}
            mode={drawerMode}
            blockOptions={blockOptions}
            onBackToBlockList={onBackToBlockList}
            onCreateBlock={onCreateBlock}
            onPatientInstructionsChange={onPatientInstructionsChange}
            onSelectBlock={onSelectBlock}
            patientInstructions={patientInstructions}
            nutritionBlockDetails={nutritionBlockDetails}
            selectedBlock={selectedBlock}
            handleUpdateUserBlock={handleUpdateUserBlock}
          />
        )}
      />
    </>
  );
};
